import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { blogs } from "../Blog";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import {
  ArrowLeft,
  Share2,
  Heart,
  Bookmark,
  Clock,
  Instagram,
  Twitter,
} from "lucide-react";

/* ================= COLORS ================= */

const COLORS = {
  primary: "#546E7A",     // Dark Slate Blue (main brand color)
  accent: "#D7A19E",      // Muted Rose
  text: "#4A5A64",        // Soft text color
  bg: "#F9FBFA",          // Main background
  lightBg: "#9BB2A3",     // Light sage background
  border: "#C5B358",      // Muted gold border
};
const BlogDetail = () => {
  const { slug } = useParams();
  const blog = blogs.find((b) => b.slug === slug);

  const [progress, setProgress] = useState(0);
  const [offset, setOffset] = useState(0);

  /* ================= SCROLL ================= */

  useEffect(() => {
    const handleScroll = () => {
      const total =
        document.documentElement.scrollHeight - window.innerHeight;

      setProgress((window.scrollY / total) * 100);
      setOffset(window.scrollY * 0.3);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!blog)
    return (
      <div className="h-screen flex items-center justify-center font-serif italic text-slate-400">
        Article vanished...
      </div>
    );

  return (
    <div
      className="min-h-screen selection:text-white"
      style={{ background: COLORS.bg }}
    >
      <Helmet>
        <title>{blog.title} | Premium Health Journal</title>
      </Helmet>

      {/* ================= PROGRESS BAR ================= */}

      <div className="fixed top-0 left-0 w-full h-[3px] bg-slate-100 z-[1000]">
        <div
          className="h-full transition-all duration-150 ease-out"
          style={{
            width: `${progress}%`,
            background: COLORS.accent,
          }}
        />
      </div>

      <Header />

      {/* ================= HERO SECTION ================= */}

      <header className="relative h-[85vh] w-full overflow-hidden flex items-center justify-center">
        <img
          src={blog.image}
          alt={blog.title}
          style={{
            transform: `scale(1.1) translateY(${offset}px)`,
          }}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-100 ease-linear"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#FAF9F6]" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div
            className="inline-block px-4 py-1.5 rounded-full backdrop-blur-md border text-white text-[10px] font-bold uppercase tracking-[0.3em] mb-8"
            style={{
              background: "rgba(255,255,255,0.1)",
              borderColor: "rgba(255,255,255,0.2)",
            }}
          >
            {blog.category || "Clinical Journal"}
          </div>

          <h1
            className="text-5xl md:text-8xl font-serif italic leading-[1.1] drop-shadow-2xl"
            style={{ color: "#000" }}
          >
            {blog.title}
          </h1>
        </div>
      </header>

      {/* ================= MAIN LAYOUT ================= */}

      <div className="max-w-7xl mx-auto px-6 -mt-24 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* LEFT SIDEBAR */}

          <aside className="lg:col-span-2 hidden lg:block">
            <div className="sticky top-32 space-y-12">
              <Link
                to="/blogs"
                className="group flex items-center gap-3 transition-colors"
                style={{ color: "#94a3b8" }}
              >
                <div className="p-2 rounded-full border border-slate-200 group-hover:border-[#D7A19E]">
                  <ArrowLeft size={14} />
                </div>

                <span className="text-[10px] font-bold uppercase tracking-widest">
                  Back
                </span>
              </Link>

              <div className="space-y-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-300">
                  Reading Time
                </p>

                <div
                  className="flex items-center gap-2 font-serif italic text-xl"
                  style={{ color: COLORS.primary }}
                >
                  <Clock size={18} style={{ color: COLORS.accent }} />

                  {blog.readTime || "08 min"}
                </div>
              </div>

              <div className="pt-8 border-t border-slate-100 flex flex-col gap-5">
                {[Heart, Bookmark, Share2].map((Icon, i) => (
                  <button
                    key={i}
                    className="w-10 h-10 rounded-full bg-white shadow-sm border border-slate-50 flex items-center justify-center transition-all"
                    style={{ color: "#64748b" }}
                  >
                    <Icon size={18} />
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* ================= BLOG CONTENT ================= */}

          <main className="lg:col-span-7 bg-white rounded-[3rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.05)] p-8 md:p-16">
            <article className="prose prose-stone prose-xl max-w-none">
              {/* EXCERPT */}

              <p
                className="first-letter:text-7xl first-letter:font-serif first-letter:italic first-letter:mr-3 first-letter:float-left text-2xl font-light leading-relaxed italic mb-16 border-b border-slate-50 pb-12"
                style={{ color: COLORS.primary }}
              >
                {blog.excerpt ||
                  "Compassionate medical guidance meets technical excellence. We explore the intersection of wellness and professional care."}
              </p>

              <div
                className="leading-[2] font-light prose-headings:font-serif prose-headings:italic prose-img:rounded-3xl prose-img:my-12"
                style={{ color: COLORS.text }}
              >
                {blog.content()}
              </div>

              {/* TAGS */}

              <div className="mt-20 pt-10 border-t border-slate-100 flex flex-wrap gap-2">
                {["OBGYN", "Wellness", "Clinical"].map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest border cursor-pointer transition-all"
                    style={{
                      background: COLORS.lightBg,
                      color: COLORS.primary,
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </article>
          </main>

          {/* ================= RIGHT SIDEBAR ================= */}

          <aside className="lg:col-span-3 space-y-8">
            {/* AUTHOR CARD */}

            <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-50">
              <img
                src="https://i.pravatar.cc/150?u=dr"
                className="w-20 h-20 rounded-2xl object-cover mb-6"
                alt="author"
              />

              <p
                className="text-[10px] font-bold uppercase tracking-widest mb-2"
                style={{ color: COLORS.accent }}
              >
                Editor In Chief
              </p>

              <h4
                className="font-serif italic text-2xl mb-4"
                style={{ color: COLORS.text }}
              >
                {blog.author}
              </h4>

              <p className="text-sm text-slate-500 leading-relaxed font-light mb-6">
                Advancing women's health through digital storytelling and
                clinical precision.
              </p>

              <div className="flex gap-4 text-slate-300">
                <Instagram
                  size={18}
                  className="cursor-pointer"
                  style={{ color: COLORS.accent }}
                />
                <Twitter
                  size={18}
                  className="cursor-pointer"
                  style={{ color: COLORS.accent }}
                />
              </div>
            </div>

            {/* CTA */}

            <div
              className="p-8 rounded-[2.5rem] text-white"
              style={{ background: COLORS.primary }}
            >
              <h5 className="font-serif italic text-xl mb-4">
                Need Expert Advice?
              </h5>

              <p className="text-white/70 text-sm font-light mb-6">
                Schedule a private consultation with our specialized medical
                team.
              </p>

              <Link to="/contact-us">
                <button
                  className="w-full py-4 rounded-xl text-xs font-bold uppercase tracking-widest transition-all"
                  style={{ background: COLORS.accent }}
                >
                  Book Now
                </button>
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {/* ================= MOBILE FLOAT BAR ================= */}

      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] w-[90%] max-w-md">
        <div
          className="backdrop-blur-xl border border-white/10 rounded-full px-8 py-4 flex items-center justify-between shadow-2xl"
          style={{ background: COLORS.text }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white text-[10px] font-bold">
              {Math.round(progress)}%
            </div>

            <div className="h-6 w-[1px] bg-white/10" />

            <span className="text-white/60 text-[10px] uppercase font-bold hidden sm:block">
              Reading
            </span>
          </div>

          <div className="flex items-center gap-6 text-white/80">
            <Heart size={20} />
            <Bookmark size={20} />
            <Share2 size={20} />
          </div>
        </div>
      </div>

      <div className="mt-32">
        <Footer />
      </div>
    </div>
  );
};

export default BlogDetail;